import Vue from 'vue';
import Router from 'vue-router';
import store from 'Store';
import analytics from 'Analytics';
import { userLog } from 'Api/user';
import { isSkipRouteLoading } from 'Utils/router';
import routes from './routes';
import { scrollBehavior } from './scroll-behavior';
import { resolvePageDependencies } from './dependency-manager';

Vue.use(Router);

const router = new Router({
  mode: 'history',
  routes,
  scrollBehavior,
});

let pageLoadTimeoutAlertId;

function clearPageLoadTimeoutAlert() {
  clearTimeout(pageLoadTimeoutAlertId);
}

/**
 * Set timeout to alert to google log about too long page loading
 * @param to - to route
 * @param from - from route
 */
function setPageLoadTimeoutAlert(to = {}, from = {}) {
  const timeout = 10000;
  clearPageLoadTimeoutAlert();
  pageLoadTimeoutAlertId = setTimeout(() => {
    userLog('PAGE LOAD TIMEOUT', { timeout, to: to.fullPath, from: from.fullPath });
    if (window.__prerender) console.error(`page resolving more than ${timeout}ms`); // eslint-disable-line
  }, timeout);
}

router.beforeEach((to, from, next) => {
  // Do not allow user change complete social registration page if it didn't confirm privacy
  if (
    store.getters['login/isLoggedIn']
    && from.name === 'sign-up-social'
    // Excluded routes
    && !['terms', 'privacy'].includes(to.name)
    && !store.getters['profile/isPrivacyConfirmed']
  ) {
    next(false);
    return;
  }

  if (to.name !== from.name && !isSkipRouteLoading(to, from)) {
    store.dispatch('router/setRouteLoading', true);
    setPageLoadTimeoutAlert(to, from);
  }

  if (['sign-in', 'sign-up', 'sign-up-social', 'sign-up-social-accreditation'].includes(from.name)) { // if the page we are currently on is sign-in/sign-up flow
    try {
      const newRoute = new URL(to.fullPath, window.location.origin); // construct a new URL using our current origin
      if (window.location.hostname.toLowerCase() === newRoute.hostname.toLowerCase()) { // if the new url hostname matches our current hostname, continue. Otherwise, stop the redirect
        next();
      } else {
        next({ name: 'primary-offerings' });
      }
      return;
    // eslint-disable-next-line no-empty
    } catch (err) {}
    next({ name: 'primary-offerings' });
    return;
  }

  next();
});

router.beforeResolve((to, from, next) => {
  if (!isSkipRouteLoading(to, from)) {
    store.dispatch('router/setRouteLoading', true);
    setPageLoadTimeoutAlert(to, from);
  }
  resolvePageDependencies(to, from, next);
});

router.afterEach((to, from) => {
  clearPageLoadTimeoutAlert();
  store.dispatch('router/setRouteLoading', false);
  store.dispatch('router/setPrevRoute', from);
  analytics.triggerPage(to);
});

router.onError((e) => {
  clearPageLoadTimeoutAlert();
  if (e.message === 'cancel') {
    store.dispatch('router/setRouteLoading', false);
  }
});

export default router;
